/**
 * Dokumentai client — supplier-invoice scan workflow.
 *
 * Transport split (by design): the FILE and all state go to the .NET API
 * (multipart upload, status machine, review, confirm); recognition is
 * triggered through the AiBridge POST /scan (same transport contract as
 * insights.ts: Bearer from the in-memory token, X-Workspace-Id always) — the
 * bridge fetches the bytes from the API itself and posts the result back, so
 * the browser never re-sends the file and the tab can close mid-scan.
 */
import { api, getToken } from './client';

// Dev talks to the bridge port directly; the production build uses the
// same-origin /ai prefix (nginx routes /ai/ to the bridge container).
const AI_BRIDGE_URL: string = import.meta.env.VITE_AI_BRIDGE_URL
  ?? (import.meta.env.DEV ? `http://${window.location.hostname}:5291` : '/ai');
const WORKSPACE_ID_KEY = 'a5core_workspace_id';

// ── DTOs (mirror A5Core.Application.UseCases.Documents) ──

export interface DocumentExtractionItem {
  name: string;
  unit: string;
  qty: string;
  unitPrice: string;
  vatRate: string;
  sum: string;
}

export interface DocumentExtractionInvoice {
  supplierName: string;
  supplierCode: string;
  supplierVatCode: string;
  supplierAddress: string;
  supplierCountry: string;
  supplierPhone: string;
  supplierEmail: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  currency: string;
  netTotal: string;
  totalVat: string;
  total: string;
  items: DocumentExtractionItem[];
  note: string;
}

export interface DocumentCrosscheck {
  check: string;
  detail: string;
}

export interface DocumentExtraction {
  version: number;
  invoice: DocumentExtractionInvoice;
  crosscheck: DocumentCrosscheck[];
  scannedAtUtc: string;
}

export type DocumentStatus =
  | 'Uploaded' | 'Recognizing' | 'Recognized'
  | 'RecognitionFailed' | 'Confirmed' | 'Rejected';

export interface ScannedDocumentDto {
  id: number;
  status: DocumentStatus;
  fileName: string;
  mediaType: string;
  fileSizeBytes: number;
  model: string | null;
  rounds: number;
  latencyMs: number;
  errorCode: string | null;
  invoiceJournalEntryId: number | null;
  createdAtUtc: string;
  recognizedAtUtc: string | null;
  confirmedAtUtc: string | null;
  extraction: DocumentExtraction | null;
}

export interface ConfirmScannedLine {
  name: string;
  unit: string;
  quantity: number;
  unitPrice: number;
  vatRate: number;   // FRACTION (0.21 = 21%)
  sum: number;
  account: number | null;
  vatAccount: number | null;
}

export interface ConfirmScannedDocumentRequest {
  documentId: number;
  personId: number;
  content: string | null;
  defaultExpenseAccount: number;
  defaultVatAccount: number | null;
  lines: ConfirmScannedLine[];
}

// ── API (state) ──

export const documentsApi = {
  upload: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.upload<ScannedDocumentDto>('/documents', form);
  },

  list: () => api.get<ScannedDocumentDto[]>('/documents'),
  getById: (id: number) => api.get<ScannedDocumentDto>(`/documents/${id}`),
  fileUrl: (id: number) => `/api/documents/${id}/file`,

  startRecognition: (id: number) => api.post<ScannedDocumentDto>(`/documents/${id}/recognize`),

  review: (id: number, extraction: DocumentExtraction) =>
    api.put<ScannedDocumentDto>(`/documents/${id}`, extraction),

  confirm: (request: ConfirmScannedDocumentRequest) =>
    api.post<ScannedDocumentDto>(`/documents/${request.documentId}/confirm`, request),

  reject: (id: number) => api.post<ScannedDocumentDto>(`/documents/${id}/reject`),

  /**
   * Trigger recognition via the bridge. Returns a small summary; the
   * authoritative state comes from refetching the document (the bridge has
   * already persisted the result by the time this resolves).
   */
  scan: (documentId: number): Promise<{ documentId: number; status: string; rounds: number; model: string; crosscheckCount: number }> =>
    fetch(`${AI_BRIDGE_URL}/scan`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
        ...(localStorage.getItem(WORKSPACE_ID_KEY)
          ? { 'X-Workspace-Id': localStorage.getItem(WORKSPACE_ID_KEY) as string }
          : {}),
      },
      body: JSON.stringify({ documentId }),
    }).then(async res => {
      if (!res.ok) throw new Error(`Scan failed (${res.status}): ${await res.text().catch(() => '')}`);
      return res.json();
    }),
};

/** Extraction vatRate percentage string ("21") → fraction (0.21). */
export function vatFraction(percent: string): number {
  const pct = parseFloat(percent);
  if (!Number.isFinite(pct) || pct <= 0) return 0;
  return pct >= 1 ? pct / 100 : pct;
}

/** Parse an extraction number string invariantly; absent/unparsable → 0. */
export function parseNum(s: string): number {
  const v = parseFloat(s);
  return Number.isFinite(v) ? v : 0;
}
